import { ethers } from "ethers";

const DEFAULT_DECIMALS = 18;

/**
 * Rewrite exponential notation as plain decimal notation.
 *
 * `String(1e-7)` yields "1e-7" and `(1e21).toFixed(2)` yields "1e+21"; both are
 * rejected by parseUnits, so any value that might have come from float maths
 * has to be expanded first.
 */
function expandExponential(str) {
    const m = /^([+-]?)(\d*)(?:\.(\d*))?[eE]([+-]?\d+)$/.exec(str);
    if (!m) return str;

    const [, sign, intPart, fracPart = "", expStr] = m;
    const digits = intPart + fracPart;
    const pointPos = intPart.length + parseInt(expStr, 10);

    if (pointPos <= 0) return `${sign}0.${"0".repeat(-pointPos)}${digits}`;
    if (pointPos >= digits.length) {
        return sign + digits + "0".repeat(pointPos - digits.length);
    }
    return `${sign}${digits.slice(0, pointPos)}.${digits.slice(pointPos)}`;
}

/**
 * Clamp a decimal amount to what a token can actually represent.
 *
 * `ethers.parseUnits` throws NUMERIC_FAULT/underflow the moment a value carries
 * more fraction digits than the token's `decimals`. That is easy to hit two
 * ways:
 *
 *   - float maths, e.g. `(value * lpUserBalance) / 100` stringifies to
 *     "0.00023750006243738724" — 20 fraction digits against an 18-decimal LP
 *     token;
 *   - a user typing more precision into an input than the token supports, which
 *     any 6-decimal token (USDT here) makes trivially reachable.
 *
 * Extra digits are **truncated, never rounded**. Rounding up can push an amount
 * past the balance actually held, which makes the transaction revert on-chain
 * instead of failing here.
 */
export function toUnitString(amount, decimals) {
    const parsed = Number(decimals);
    const places =
        Number.isFinite(parsed) && parsed >= 0
            ? Math.floor(parsed)
            : DEFAULT_DECIMALS;

    if (amount === null || amount === undefined) return "0";

    let s = typeof amount === "string" ? amount.trim() : String(amount);
    if (s === "" || s === "." || s === "-" || s === "+") return "0";
    if (/[eE]/.test(s)) s = expandExponential(s);
    if (!/^[+-]?\d*\.?\d*$/.test(s)) return "0";

    const negative = s.startsWith("-");
    s = s.replace(/^[+-]/, "");

    let [int = "0", frac = ""] = s.split(".");
    if (int === "") int = "0";
    frac = frac.slice(0, places);

    const body = places === 0 || frac === "" ? int : `${int}.${frac}`;
    return negative ? `-${body}` : body;
}

/**
 * parseUnits that cannot throw on precision. Use this anywhere the amount came
 * from float arithmetic or a free-text input.
 */
export function safeParseUnits(amount, decimals) {
    const parsed = Number(decimals);
    const places =
        Number.isFinite(parsed) && parsed >= 0
            ? Math.floor(parsed)
            : DEFAULT_DECIMALS;
    return ethers.parseUnits(toUnitString(amount, places), places);
}
